import React, { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import { useButton, useDispatch, useI18n, useSelector, useWindowSize } from '~Hooks';
import Journal from '~Utils/journal';
import StaffSignIn from '~Scenes/Tables/Components/StaffSignIn';
import UndoIcon from '~Assets/icons/undo.svg';
import { useHistory, useLocation } from 'react-router';
import { useCart } from '~Components/CartProvider';
import { BlackButton, Button } from '~Styles';
import Icon from '~ComponentsImproved/Icon';
import TypeChanger from '~ComponentsImproved/TSR/TypeChanger';

const Wrapper = styled.div`
  position: absolute;
  z-index: 3;
  width: calc(100% - 64px);
  display: flex;
  flex-flow: row nowrap;
  height: 104px;
  bottom: 0;
  left: 64px;
  padding: 32px 12px 16px 12px;
  box-sizing: border-box;
  background: linear-gradient(180deg, rgba(47, 53, 61, 0) 0%, #2f353d 100%);
  justify-content: flex-end;

  button {
    margin: 0 4px;
  }
`;

interface Props {
  venueId: string;
  undo: () => void;
  itemsInCart: boolean;
  parkOrder: () => void;
  showCourseTypeWait: boolean | undefined;
  isTsr?: boolean;
}

const BottomButtons = ({
  venueId,
  undo,
  itemsInCart,
  parkOrder,
  showCourseTypeWait,
  isTsr,
}: Props) => {
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const windowSize = useWindowSize();
  const history = useHistory();
  const location = useLocation();
  const cart = useCart();

  const bongPrintingHalted = useSelector((state) => state.BongPrintingHalted);
  const bongGroupCount = useSelector((state) => state.POSBongGroups.length);

  const [showCourseTypeModal, setShowCourseTypeModal] = useState<boolean>(false);

  const toggleBongPause = useCallback(() => {
    if (bongPrintingHalted) {
      Journal.write('Bong Printing', 'Resumed');
      dispatch({
        type: 'RESUME_BONG_PRINTING',
      });
    } else {
      Journal.write('Bong Printing', 'Halted');
      dispatch({
        type: 'HALT_BONG_PRINTING',
      });
    }
  }, [bongPrintingHalted]);

  const isSmallScreen = windowSize.width;
  const showParkedOrders = () => {
    if (location.pathname.includes('multi-pos')) {
      history.push(`/venue/${venueId}/multi-pos/parked-orders`);
      return;
    }

    history.push(`/venue/${venueId}/pos/parked-orders`);
  };

  const waitButton = useButton({
    onClick: () => {
      setShowCourseTypeModal(true);
    },
  });

  const parkOrderButton = useButton({
    onClick: () => {
      itemsInCart ? parkOrder() : showParkedOrders();
    },
  });

  const undoButton = useButton({
    onClick: undo,
    disabled: !itemsInCart,
  });

  const collectButton = useButton({
    onClick: toggleBongPause,
  });

  return (
    <Wrapper>
      {!isTsr && <StaffSignIn />}
      {!isTsr && (
        <BlackButton
          {...parkOrderButton}
          style={{
            width: '152px',
          }}>
          {itemsInCart ? i18n('POS.Park').toUpperCase() : i18n('POS.Parked').toUpperCase()}
        </BlackButton>
      )}
      {!isTsr && (
        <BlackButton
          {...collectButton}
          style={{
            width: '152px',
          }}>
          {bongPrintingHalted
            ? i18n('POS.Collecting').toUpperCase() + ' (' + bongGroupCount + ')'
            : i18n('POS.Collect').toUpperCase()}
        </BlackButton>
      )}
      {showCourseTypeWait && <Button {...waitButton}>{i18n('POS.Wait')}</Button>}

      <BlackButton
        {...undoButton}
        style={{
          width: isSmallScreen ? '56px' : '152px',
        }}>
        <Icon
          src={UndoIcon}
          style={{
            marginRight: isSmallScreen ? '0' : '8px',
          }}
        />
        {isSmallScreen ? '' : i18n('POS.Undo').toUpperCase()}
      </BlackButton>

      {showCourseTypeModal && (
        <TypeChanger
          onChange={(courseType) => {
            cart.addMessageToTicket(
              'wait' + courseType.id,
              i18n('POS.Wait') + ' ' + courseType.label,
              courseType.id,
            );
            setShowCourseTypeModal(false);
          }}
          onClose={() => setShowCourseTypeModal(false)}
        />
      )}
    </Wrapper>
  );
};

export default React.memo(BottomButtons);
